import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';

const SalesPromoPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [quotas, setQuotas] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [form, setForm] = useState({
    campaign_id: '',
    opportunity_id: '',
  });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [campaignsResponse, quotasResponse, promoResponse, opportunitiesResponse] = await Promise.all([
        axiosInstance.get('sales/promo/campaigns/'),
        axiosInstance.get('sales/promo/my-quotas/'),
        axiosInstance.get('sales/promo/codes/', { params: { mine: true } }),
        axiosInstance.get('sales/opportunities/', { params: { status: 'open' } }),
      ]);

      const nextCampaigns = campaignsResponse.data?.campaigns || [];
      const nextQuotas = quotasResponse.data?.quotas || [];
      setCampaigns(nextCampaigns);
      setQuotas(nextQuotas);
      setPromoCodes(promoResponse.data?.promo_codes || []);
      setOpportunities(opportunitiesResponse.data?.opportunities || []);
      setForm(previous => ({
        campaign_id: previous.campaign_id || (nextCampaigns[0] ? String(nextCampaigns[0].id) : ''),
        opportunity_id: previous.opportunity_id || '',
      }));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_PROMO'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [t]);

  const reservedPromos = useMemo(
    () => promoCodes.filter(item => item.status === 'reserved').slice(0, 20),
    [promoCodes]
  );

  const quotaSummary = useMemo(() => {
    return quotas.reduce((accumulator, item) => ({
      total: accumulator.total + Number(item.quota_total || 0),
      reserved: accumulator.reserved + Number(item.quota_reserved || 0),
      used: accumulator.used + Number(item.quota_used || 0),
      remaining: accumulator.remaining + Number(item.quota_remaining || 0),
    }), { total: 0, reserved: 0, used: 0, remaining: 0 });
  }, [quotas]);

  const reservePromo = async event => {
    event.preventDefault();
    if (!form.campaign_id) {
      setError(t('SALES.ERROR_CAMPAIGN_REQUIRED'));
      return;
    }
    if (!form.opportunity_id) {
      setError(t('SALES.ERROR_OPPORTUNITY_REQUIRED'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post('sales/promo/codes/reserve/', {
        campaign_id: Number(form.campaign_id),
        opportunity_id: Number(form.opportunity_id),
      });
      setSuccess(t('SALES.PROMO_RESERVED_SUCCESS'));
      setForm(previous => ({ ...previous, opportunity_id: '' }));
      await loadData();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_ASSIGN_PROMO'));
    } finally {
      setSaving(false);
    }
  };

  const cancelPromo = async promoCode => {
    setCancellingId(String(promoCode.id));
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post(`sales/promo/codes/${promoCode.id}/cancel/`);
      setSuccess(t('SALES.PROMO_CANCELLED_SUCCESS'));
      await loadData();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CANCEL_PROMO'));
    } finally {
      setCancellingId('');
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.PROMO')} className="mb-3" />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loading ? (
        <Card className="p-4 text-center"><Spinner animation="border" /></Card>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={6} xl={3}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.AVAILABLE_PROMOS')}</div><div className="fs-2 fw-bold text-primary">{quotaSummary.remaining}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.RESERVED_THIS_MONTH')}</div><div className="fs-2 fw-bold text-warning">{quotaSummary.reserved}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.USED_THIS_MONTH')}</div><div className="fs-2 fw-bold text-success">{quotaSummary.used}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.ACTIVE_CAMPAIGNS')}</div><div className="fs-2 fw-bold">{campaigns.filter(item => item.is_active).length}</div></Card.Body></Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col xl={5}>
              <Card>
                <Card.Header className="fw-semi-bold">{t('SALES.RESERVE_PROMO')}</Card.Header>
                <Card.Body>
                  <Form onSubmit={reservePromo}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('SALES.CAMPAIGN')}</Form.Label>
                      <Form.Select value={form.campaign_id} onChange={event => setForm(previous => ({ ...previous, campaign_id: event.target.value }))}>
                        <option value="">{t('SALES.SELECT_ONE')}</option>
                        {campaigns.filter(item => item.is_active).map(item => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>{t('SALES.OPPORTUNITY')}</Form.Label>
                      <Form.Select value={form.opportunity_id} onChange={event => setForm(previous => ({ ...previous, opportunity_id: event.target.value }))}>
                        <option value="">{t('SALES.SELECT_ONE')}</option>
                        {opportunities.map(item => (
                          <option key={item.id} value={item.id}>{item.title} · {item.company_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Button type="submit" disabled={saving || !form.campaign_id || !form.opportunity_id}>
                      {saving ? t('SALES.SAVING') : t('SALES.RESERVE_PROMO')}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="mb-3">
                <Card.Header className="fw-semi-bold">{t('SALES.MY_CAMPAIGNS')}</Card.Header>
                <Card.Body className="pt-2">
                  <Table responsive hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>{t('SALES.CAMPAIGN')}</th>
                        <th>{t('SALES.QUOTA')}</th>
                        <th>{t('SALES.USED')}</th>
                        <th>{t('SALES.RESERVED')}</th>
                        <th>{t('SALES.REMAINING')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotas.length ? quotas.map(item => (
                        <tr key={item.campaign_id}>
                          <td>
                            <div className="fw-semi-bold">{item.campaign_name}</div>
                            <div className="text-600 fs--2">{item.discount_value} · {item.discount_type}</div>
                          </td>
                          <td>{item.quota_total}</td>
                          <td>{item.quota_used}</td>
                          <td>{item.quota_reserved}</td>
                          <td><Badge bg="soft-primary" text="primary">{item.quota_remaining}</Badge></td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="text-center text-muted py-3">{t('SALES.EMPTY_QUOTA')}</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="fw-semi-bold">{t('SALES.MY_RESERVED_PROMOS')}</Card.Header>
                <Card.Body className="pt-2">
                  <Table responsive hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>{t('SALES.PROMO_CODE')}</th>
                        <th>{t('SALES.CAMPAIGN')}</th>
                        <th>{t('SALES.OPPORTUNITY')}</th>
                        <th>{t('SALES.STATUS')}</th>
                        <th>{t('SALES.TABLE_ACTIONS')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservedPromos.length ? reservedPromos.map(item => (
                        <tr key={item.id}>
                          <td className="fw-semi-bold">{item.code}</td>
                          <td>{item.campaign_name}</td>
                          <td>
                            {item.opportunity_id ? (
                              <Button size="sm" variant="link" className="p-0" onClick={() => navigate(`/sales/opportunity/${item.opportunity_id}`)}>
                                {item.opportunity_title || `#${item.opportunity_id}`}
                              </Button>
                            ) : '-'}
                          </td>
                          <td><Badge bg="warning">{item.status}</Badge></td>
                          <td>
                            <Button size="sm" variant="outline-danger" disabled={cancellingId === String(item.id)} onClick={() => cancelPromo(item)}>
                              {cancellingId === String(item.id) ? t('SALES.SAVING') : t('SALES.REMOVE_PROMO')}
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="text-center text-muted py-3">{t('SALES.EMPTY_RESERVED_CODES')}</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SalesPromoPage;